/**
 * StarRating - shows a rating, or lets someone choose one
 * -------------------------------------------------------
 * Two ways to use it:
 *
 *   <StarRating value={4.5} count={12} />          just showing a rating
 *   <StarRating value={rating} onChange={setR} />  letting someone pick one
 */

import { Star } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

export default function StarRating({
  value = 0,
  count = null,
  onChange = null,
  size = 16,
  showEmpty = true,
}) {
  const { t } = useI18n();
  const editable = typeof onChange === 'function';

  // ---- Pickable version (used in the review form) ----
  if (editable) {
    return (
      <div className="star-input" role="radiogroup" aria-label={t('misc.starRating')}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} star${star === 1 ? '' : 's'}`}
            className={`star-button${star <= value ? ' filled' : ''}`}
            onClick={() => onChange(star)}
          >
            <Star size={size + 12} fill={star <= value ? 'currentColor' : 'none'} />
          </button>
        ))}
      </div>
    );
  }

  // ---- Read-only version ----
  const rounded = Math.round(value);

  if (!value && !showEmpty) {
    return <span className="text-sm text-muted">{t('review.noneYet')}</span>;
  }

  return (
    <span className="stars" aria-label={`Rated ${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          fill={star <= rounded ? 'currentColor' : 'none'}
          strokeWidth={2}
          aria-hidden="true"
        />
      ))}
      {count !== null && (
        <span className="stars-label">
          {count > 0 ? `${value.toFixed(1)} (${count})` : t('review.noneYet')}
        </span>
      )}
    </span>
  );
}
